import { memo, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Alert,
  Button,
  SpaceBetween,
  StatusIndicator,
  Textarea,
} from '@amzn/awsui-components-core';
import { calculatePosition, Position } from '../../v2-content-editor/PopoverDialog';
import { useCommentCreationPopoverViewModel } from './CommentCreationPopoverViewModel';
import styles from './CommentCreationPopover.module.scss';

export interface CommentCreationPopoverProps {
  /** Page-absolute rect where the popover should anchor to. */
  readonly target: DOMRect;
  /** The selected text displayed as context. */
  readonly textHint: string;
  /** Called when the user submits the comment. */
  readonly onSubmit: (body: string) => Promise<void>;
  /** Called when the popover is dismissed. */
  readonly onDismiss: () => void;
}

export const CommentCreationPopover = memo(function CommentCreationPopover({
  target,
  textHint,
  onSubmit,
  onDismiss,
}: CommentCreationPopoverProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position | null>(null);

  const vm = useCommentCreationPopoverViewModel({ textHint, onSubmit, onDismiss });

  // When target rect changes, recalculate placement and focus the textarea.
  // Focus must happen after position is set, otherwise the browser scrolls to the wrong location.
  useEffect(() => {
    if (!dialogRef.current) return;
    setPosition(calculatePosition(target, dialogRef.current));

    requestAnimationFrame(() => {
      dialogRef.current?.querySelector('textarea')?.focus();
    });
  }, [target]);

  // When clicking outside, dismiss the popover.
  const vmHandleDismiss = vm.handleDismiss;
  useEffect(() => {
    function handleMouseDown(event: MouseEvent) {
      if (
        dialogRef.current &&
        event.target instanceof Node &&
        !dialogRef.current.contains(event.target)
      ) {
        vmHandleDismiss();
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [vmHandleDismiss]);

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-label="Add comment"
      className={styles.overlay}
      style={
        position
          ? { top: position.top, left: position.left }
          : { visibility: 'hidden', top: 0, left: 0 }
      }
    >
      <div className={styles.container}>
        {/* Arrow */}
        <div
          className={`${styles.arrow} ${position?.placement === 'top' ? styles.arrowTop : styles.arrowBottom}`}
          style={{ left: position ? position.arrowLeft - 6 : '50%' }}
        />

        {/* Header */}
        <div className={styles.header}>
          <span className={styles.title}>Add Comment</span>
          <Button variant="icon" iconName="close" ariaLabel="Close" onClick={vm.handleDismiss} />
        </div>

        {/* Body */}
        <div className={styles.body}>
          <SpaceBetween direction="vertical" size="s">
            <div className={styles.blockquote}>{textHint}</div>

            <Textarea
              value={vm.commentBody}
              onChange={({ detail }) => vm.setCommentBody(detail.value)}
              placeholder="Write your comment..."
              rows={vm.computedRows}
              disabled={vm.isSubmitting}
            />

            {vm.error && (
              <Alert type="error" dismissible onDismiss={vm.dismissError}>
                {vm.error}
              </Alert>
            )}
          </SpaceBetween>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <SpaceBetween direction="vertical" size="xs">
            {vm.disabledReason && (
              <StatusIndicator type="error">{vm.disabledReason}</StatusIndicator>
            )}
            <Button
              variant="primary"
              loading={vm.isSubmitting}
              disabled={vm.isSubmitDisabled}
              onClick={vm.handleSubmit}
            >
              Add Comment
            </Button>
          </SpaceBetween>
        </div>
      </div>
    </div>,
    document.body,
  );
});
